/**
 * formatters.js
 * CCH Care — shared date/time and amendment formatting helpers
 * Last Modified: 2026-06-25 (v5: registered facility display timezone —
 *   setDisplayTimezone() makes facility-local rendering the app-wide
 *   default so call sites no longer have to thread facilityTz by hand)
 *
 * Why this file exists:
 * ───────────────────────────────────────────────────────────────
 * The backend (PHP `date('Y-m-d H:i:s')` and MySQL `NOW()`) emits
 * timestamps as "YYYY-MM-DD HH:MM:SS" strings with NO timezone
 * designator. When the browser receives these and runs them through
 * `new Date(...)`, the ECMAScript spec leaves the interpretation
 * implementation-defined for space-separated strings — and modern
 * Chrome/Firefox/Safari treat them as UTC. The browser then converts
 * to the user's local time for display.
 *
 * Result: a timestamp the server stamped at 07:20 (server-local CDT)
 * gets re-interpreted as 07:20 UTC, then displayed in Eastern as 03:20.
 * That's the "5-hour bug" — the fix is to parse server datetimes as
 * the SERVER's known timezone, not as UTC.
 *
 * For display, healthcare documentation should show times in the
 * FACILITY's local timezone — that's the operationally meaningful
 * frame of reference for clinical events ("the meal was given at
 * 7:20 AM" means 7:20 in the facility, regardless of where the
 * viewer's browser is).
 *
 * Display timezone resolution (v5):
 * ───────────────────────────────────────────────────────────────
 * The format helpers accept an optional `facilityTz` argument, but
 * the app shouldn't have to pass it at every call site (it never did,
 * which meant every timestamp silently rendered in the VIEWER's
 * browser zone — wrong for any off-site reviewer of a facility in a
 * different zone, e.g. a US-based supervisor reviewing a Jamaica
 * facility). Instead, the active facility's zone is REGISTERED once
 * via setDisplayTimezone() when auth resolves (see AuthProvider), and
 * every helper falls back to it. Resolution order, per call:
 *
 *     facilityTz argument        (explicit override, rarely needed)
 *  ?? registered display zone    (set by setDisplayTimezone)
 *  ?? undefined                  → Intl uses the viewer's browser zone
 *
 * The browser-zone fallback only applies before a facility zone has
 * been registered (pre-login, or if the facilities payload ever lacks
 * a timezone) — a safe degradation, not the steady state.
 *
 * IMPORTANT — date-only values:
 * These helpers are for SERVER DATETIMES ("YYYY-MM-DD HH:MM:SS"). A
 * pure calendar date ("YYYY-MM-DD" shift_date with no time) must NOT
 * be run through parseServerDt / the registered zone — it has no
 * time-of-day and no server frame, and applying a zone offset can roll
 * it across a day boundary. Render those as `new Date(d + 'T00:00:00')
 * .toLocaleDateString(...)` at the call site (see the local formatDate
 * helpers in the dashboard banners and ADLFlowSheetPage header).
 *
 * All helpers are DST-correct via Intl.DateTimeFormat, which uses
 * the IANA timezone database under the hood.
 *
 * ───────────────────────────────────────────────────────────────
 * Public API:
 *
 * Display-zone registration (2026-06-25):
 *   setDisplayTimezone(tz)                — register active facility zone
 *   getDisplayTimezone()                  → current registered zone | null
 *
 * Inbound (parsing server datetimes for display):
 *   parseServerDt(input)                  → Date | null
 *   formatTime(input, facilityTz?)        → "7:20 AM"
 *   formatDate(input, facilityTz?)        → "Apr 29, 2026"
 *   formatShortDate(input, facilityTz?)   → "4/29"
 *   formatDateTime(input, facilityTz?)    → "Apr 29, 2026 · 7:20 AM"
 *   formatRelative(input)                 → "3m ago" / "in 2h"
 *
 * Outbound (form input pre-fill, submission, today's date):
 *   formatForDatetimeLocal(input)         → "2026-04-29T07:20"
 *      (for <input type="datetime-local"> value; pass null/undefined for "now")
 *   toServerDt(input)                     → "2026-04-29 07:20:00"
 *      (inverse of parseServerDt: a viewer-local datetime-local string or
 *       Date → "YYYY-MM-DD HH:MM:SS" in SERVER_TIMEZONE, ready to POST)
 *   todayLocal()                          → "2026-04-29"
 *      (replaces broken `new Date().toISOString().split('T')[0]`)
 *
 * Amendment display (2026-05-22):
 *   formatAmendmentValue(section, field, raw) → "Refused" / "Yes" / "100%" / "—"
 *      (used by ADLAmendmentModal Current Value display and
 *       ADLAmendmentBanner old → new chips)
 *   formatAmendmentField(section, field)      → "Assistance Level" / "Had BM"
 *      (canonical labels for amendment row headings, snake_case fallback)
 *
 *   SERVER_TIMEZONE                       (constant)
 *
 * Inputs accepted by inbound date helpers:
 *   - "YYYY-MM-DD HH:MM:SS"       → treated as SERVER_TIMEZONE
 *   - "YYYY-MM-DDTHH:MM:SS"       → treated as SERVER_TIMEZONE
 *   - "YYYY-MM-DDTHH:MM:SSZ"      → respected as UTC
 *   - "YYYY-MM-DDTHH:MM:SS+05:00" → respected as written
 *   - Date instance               → used as-is
 *   - null / undefined / "" / invalid → returns null / "—"
 *
 * Migration note:
 * After backend pass to ISO 8601 with explicit TZ on every endpoint,
 * `parseServerDt` becomes a one-liner around `new Date(input)` and
 * `SERVER_TIMEZONE` can be retired.
 * ───────────────────────────────────────────────────────────────
 */

import { ASSIST_LEVELS, URINE_TYPES } from '../config/constants';

export const SERVER_TIMEZONE = 'America/Chicago';

// ── Registered display timezone (v5) ───────────────────────────
// The active facility's IANA zone, registered once by AuthProvider
// via setDisplayTimezone() when the session/facility resolves. Module
// scope is intentional: there is exactly one active facility per Care
// session, so a single registered zone is the correct frame for every
// timestamp rendered in that session. Falls back to undefined (→ the
// viewer's browser zone) only before registration.
let _displayTz = null;

/**
 * Register the active facility's display timezone. Pass a valid IANA
 * zone string (e.g. 'America/Jamaica'); pass null/undefined to clear
 * (e.g. on logout) so rendering degrades to the browser zone rather
 * than a stale prior-facility zone.
 */
export function setDisplayTimezone(tz) {
  _displayTz = tz || null;
}

/** Current registered display timezone, or null if none registered. */
export function getDisplayTimezone() {
  return _displayTz;
}

// Resolve the zone to hand Intl for a given call: explicit arg wins,
// then the registered facility zone, else undefined (browser zone).
function resolveTz(facilityTz) {
  return facilityTz ?? _displayTz ?? undefined;
}

const EMPTY = '—';
const pad2 = n => String(n).padStart(2, '0');


// ── Internal: parse "YYYY-MM-DD HH:MM:SS" as SERVER_TIMEZONE ───
function parseAsSourceZone(year, month, day, hour, minute, second, sourceTz) {
  const naiveUtcMs = Date.UTC(year, month - 1, day, hour, minute, second);

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: sourceTz,
    year:   'numeric', month:  '2-digit', day:    '2-digit',
    hour:   '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(naiveUtcMs));

  const renderedAs = {};
  for (const p of parts) renderedAs[p.type] = p.value;

  const hh = parseInt(renderedAs.hour, 10) % 24;

  const renderedUtcMs = Date.UTC(
    parseInt(renderedAs.year, 10),
    parseInt(renderedAs.month, 10) - 1,
    parseInt(renderedAs.day, 10),
    hh,
    parseInt(renderedAs.minute, 10),
    parseInt(renderedAs.second, 10),
  );

  const offsetMs = naiveUtcMs - renderedUtcMs;
  return new Date(naiveUtcMs + offsetMs);
}


// ── Public: parseServerDt ──────────────────────────────────────
export function parseServerDt(input) {
  if (input == null || input === '') return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input !== 'string') return null;

  const trimmed = input.trim();

  if (/(?:Z|[+\-]\d{2}:?\d{2})$/i.test(trimmed)) {
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }

  const m = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) {
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }

  const [, y, mo, d, h, mi, s] = m;
  return parseAsSourceZone(
    parseInt(y, 10),
    parseInt(mo, 10),
    parseInt(d, 10),
    parseInt(h, 10),
    parseInt(mi, 10),
    parseInt(s ?? '0', 10),
    SERVER_TIMEZONE,
  );
}


// ── Public: format helpers (inbound, for display) ──────────────
export function formatTime(input, facilityTz) {
  const d = parseServerDt(input);
  if (!d) return EMPTY;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTz(facilityTz),
    hour:   'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

export function formatDate(input, facilityTz) {
  const d = parseServerDt(input);
  if (!d) return EMPTY;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTz(facilityTz),
    month: 'short',
    day:   'numeric',
    year:  'numeric',
  }).format(d);
}

export function formatShortDate(input, facilityTz) {
  const d = parseServerDt(input);
  if (!d) return EMPTY;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTz(facilityTz),
    month: 'numeric',
    day:   'numeric',
  }).format(d);
}

export function formatDateTime(input, facilityTz) {
  const d = parseServerDt(input);
  if (!d) return EMPTY;
  const tz = resolveTz(facilityTz);
  const date = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short',
    day:   'numeric',
    year:  'numeric',
  }).format(d);
  const time = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour:   'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
  return `${date} · ${time}`;
}


// ── Public: formatRelative ─────────────────────────────────────
export function formatRelative(input) {
  const d = parseServerDt(input);
  if (!d) return EMPTY;

  const deltaMs = d.getTime() - Date.now();
  const future  = deltaMs > 0;
  const abs     = Math.abs(deltaMs);
  const seconds = Math.round(abs / 1000);
  const minutes = Math.round(seconds / 60);
  const hours   = Math.round(minutes / 60);
  const days    = Math.round(hours / 24);

  let body;
  if (seconds < 45)        body = 'just now';
  else if (minutes < 60)   body = `${minutes}m`;
  else if (hours < 24)     body = `${hours}h`;
  else if (days < 30)      body = `${days}d`;
  else                     body = formatDate(d);

  if (body === 'just now') return body;
  return future ? `in ${body}` : `${body} ago`;
}


// ── Public: outbound helpers (form input pre-fill, submission, today) ──

/**
 * Formats a server datetime (or a Date, or null/undefined for "now")
 * as YYYY-MM-DDTHH:MM in the viewer's LOCAL time. Use for the `value`
 * of `<input type="datetime-local">`. These inputs require local
 * wall-clock time; UTC ISO strings cause an offset display bug.
 *
 * Replacement for the broken pattern:
 *   new Date(x).toISOString().slice(0, 16)
 */
export function formatForDatetimeLocal(input) {
  const d = (input == null)
    ? new Date()
    : (input instanceof Date ? input : parseServerDt(input));
  if (!d) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
         `T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Inverse of parseServerDt, for form submission. Takes a viewer-LOCAL
 * datetime-local string ("YYYY-MM-DDTHH:MM" or with seconds) — or a Date —
 * and returns a "YYYY-MM-DD HH:MM:SS" string expressed in SERVER_TIMEZONE,
 * the frame the backend stores and parseServerDt reads back. This closes
 * the round-trip: a value pre-filled with formatForDatetimeLocal() and
 * submitted through toServerDt() lands as the same wall-clock instant the
 * viewer saw, stored in the server's frame.
 *
 * A bare datetime-local string ("2026-06-04T18:52") has no timezone
 * designator, so `new Date(...)` parses it as the viewer's LOCAL time —
 * which is what we want: it's the absolute instant the nurse selected.
 * We then render that instant in SERVER_TIMEZONE for storage.
 *
 * Returns '' for null/empty/invalid so callers can omit the field.
 */
export function toServerDt(input) {
  if (input == null || input === '') return '';
  const d = (input instanceof Date) ? input : new Date(input);
  if (isNaN(d.getTime())) return '';

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SERVER_TIMEZONE,
    year:   'numeric', month:  '2-digit', day:    '2-digit',
    hour:   '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const o = {};
  for (const p of parts) o[p.type] = p.value;
  const hour = (o.hour === '24') ? '00' : o.hour; // guard V8's midnight '24' quirk

  return `${o.year}-${o.month}-${o.day} ${hour}:${o.minute}:${o.second}`;
}

/**
 * Returns today's date as "YYYY-MM-DD" in the viewer's LOCAL timezone.
 *
 * Replacement for the broken pattern:
 *   new Date().toISOString().split('T')[0]
 *   new Date().toISOString().slice(0, 10)
 *
 * Returns UTC date and rolls a day early during late-evening hours
 * in negative UTC offsets (e.g., 8pm Eastern shows tomorrow's date).
 *
 * Note: `new Date().toLocaleDateString('en-CA')` does this correctly
 * already; `todayLocal()` exists as a named, intent-revealing alternative.
 */
export function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}


// ============================================================
// Amendment value/field formatting (2026-05-22)
// Shared between ADLAmendmentModal (Current Value display) and
// ADLAmendmentBanner (old → new history). Resolves raw stored
// codes to human-readable labels, coerces boolean fields to
// Yes/No, and falls through to String(value) for unmapped fields
// so notes and other free-text columns render as-is.
// ============================================================

// Picklist label maps. Mirrors the option sets in ADLAmendmentModal —
// kept here so the banner doesn't have to import from a modal. A
// follow-up should promote both sources to constants.js once the
// form components also migrate (existing TODO in the modal header).
const POSITION_LABELS = {
  left: 'Left Side', right: 'Right Side', back: 'Supine',
  elevated: 'Elevated HOB', chair: 'Chair/OOB',
};
const REPO_ASSIST_LABELS = {
  independent: 'Independent', '1_person': '1 Person',
  '2_person': '2 Person', mechanical_lift: 'Mech. Lift', refused: 'Refused',
};
const MEAL_ASSIST_LABELS = {
  independent: 'Independent', setup: 'Setup', minimal: 'Minimal',
  moderate: 'Moderate', total: 'Total', refused: 'Refused',
};
const CONSISTENCY_LABELS = {
  normal: 'Normal', hard: 'Hard', soft: 'Soft',
  loose: 'Loose', liquid: 'Liquid',
};
const URINE_COLOR_LABELS = {
  clear: 'Clear', yellow: 'Yellow', dark_yellow: 'Dark Yellow',
  amber: 'Amber', orange: 'Orange', red: 'Red', cloudy: 'Cloudy',
};
const SKIN_RESULT_LABELS = { pass: 'Pass', fail: 'Fail / Issue' };

const fromMap     = (map) => (code) => map[code] ?? code;
const fromCodeList = (arr) => (code) => arr.find(x => x.code === code)?.label ?? code;
const yesNo = (val) => {
  if (val === 1 || val === '1' || val === true)  return 'Yes';
  if (val === 0 || val === '0' || val === false) return 'No';
  return String(val ?? '');
};

// Per-section, per-field formatters. Booleans live here too so
// fields like meals.refused render "Yes"/"No" identically in the
// modal "Current Value" pill and the banner old → new chips.
const FIELD_FORMATTERS = {
  hygiene: {
    assistance_level: fromCodeList(ASSIST_LEVELS),
  },
  meals: {
    percent_consumed: (v) => `${v}%`,
    assist_level:     fromMap(MEAL_ASSIST_LABELS),
    refused:          yesNo,
    // diet_type, supplement, notes pass through unchanged
  },
  repositioning: {
    position:     fromMap(POSITION_LABELS),
    assist_level: fromMap(REPO_ASSIST_LABELS),
    was_refused:  yesNo,
    skin_intact:  yesNo,
  },
  fluid_intake: {
    refused: yesNo,
    // fluid_type passes through (raw values already match labels)
  },
  bowel: {
    had_bm:      yesNo,
    consistency: fromMap(CONSISTENCY_LABELS),
    incontinent: yesNo,
  },
  urination: {
    output_type: fromCodeList(URINE_TYPES),
    urine_color: fromMap(URINE_COLOR_LABELS),
  },
  skin_checks: {
    result:               fromMap(SKIN_RESULT_LABELS),
    wound_care_performed: yesNo,
    nurse_notified:       yesNo,
  },
  safety_checks: {
    is_checked: yesNo,
  },
};

/**
 * Format a stored amendment value for display.
 *  - Coded fields (hygiene.assistance_level 'R' → 'Refused') resolve to labels.
 *  - Boolean fields coerce to 'Yes' / 'No'.
 *  - Unmapped fields (notes, free-text) fall through as String(value).
 *  - null/empty/undefined → '—' so the banner's old → new chips never render blank.
 */
export function formatAmendmentValue(section, fieldName, rawValue) {
  if (rawValue == null || rawValue === '') return '—';
  const fmt = FIELD_FORMATTERS[section]?.[fieldName];
  return fmt ? fmt(rawValue) : String(rawValue);
}


// Field name → display label per section. Mirrors the labels in
// ADLAmendmentModal's ALLOWED_FIELDS; kept here so the banner
// doesn't have to import from a modal. Small intentional duplication
// (~30 entries) — if it drifts, the natural fix is to promote both
// modal + formatter to a single shared source.
const FIELD_LABELS = {
  hygiene: {
    assistance_level: 'Assistance Level',
    notes:            'Notes',
  },
  meals: {
    percent_consumed: '% Consumed',
    diet_type:        'Diet Type',
    assist_level:     'Assist Level',
    fluid_intake_ml:  'Fluids at Meal (mL)',
    supplement:       'Supplement',
    refused:          'Refused',
    notes:            'Notes',
  },
  repositioning: {
    position:     'Position',
    assist_level: 'Assist Level',
    was_refused:  'Refused',
    skin_intact:  'Skin Intact',
    notes:        'Notes',
  },
  fluid_intake: {
    fluid_type: 'Fluid Type',
    amount_ml:  'Amount (ml)',
    refused:    'Refused',
    notes:      'Notes',
  },
  bowel: {
    had_bm:      'Had BM',
    consistency: 'Consistency',
    incontinent: 'Incontinent',
    notes:       'Notes',
  },
  urination: {
    output_type:          'Output Type',
    incontinent_episodes: 'Incont. Episodes',
    urine_color:          'Urine Color',
    notes:                'Notes',
  },
  skin_checks: {
    result:               'Result',
    body_location:        'Body Location',
    description:          'Description',
    wound_care_performed: 'Wound Care Done',
    wound_care_notes:     'Wound Care Notes',
    nurse_notified:       'Nurse Notified',
  },
  safety_checks: {
    is_checked: 'Checked',
    notes:      'Notes',
  },
};

/**
 * Format an amendment field's display label.
 *  - Returns the canonical label from FIELD_LABELS when known
 *    (correct acronym casing, % signs, unit suffixes).
 *  - Falls through to title-cased underscored name for unmapped
 *    fields so a new backend field still renders readably without
 *    a code change.
 */
export function formatAmendmentField(section, fieldName) {
  if (!fieldName) return '';
  const label = FIELD_LABELS[section]?.[fieldName];
  if (label) return label;
  return fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}